import React from 'react';
import { t } from '@lingui/macro';
import { DownloadableItemSource } from 'types';
import { ActionButtonProps } from 'components/ActionButton/ActionButton';
import { ActionButton } from 'components/ActionButton';
import { noop } from 'utils/noop';
import useDownloadButton from 'components/CommonUi/DownloadButton/useDownloadButton';
import { getDownloadableItem } from 'helpers';
import { PlaceholderButton } from '../PlaceholderButton/PlaceholderButton';
import usePlaceholderButton from '../PlaceholderButton/usePlaceholderButton';

export interface DownloadButtonProps
    extends Omit<ActionButtonProps, 'icon' | 'label' | 'onClick'> {
    item: DownloadableItemSource;
    onClick?: () => void;
    label?: string;
    children?: React.ReactElement;
    showIcon?: boolean;
    isPlaceholderAllowed?: boolean;
}

export const DownloadButton: React.FC<DownloadButtonProps> = ({
    item,
    onClick = noop,
    label = t`Download`,
    showIcon = true,
    isPlaceholderAllowed = false,
    ...props
}) => {
    const downloadableItem = getDownloadableItem(item);

    const {
        isLoading,
        handleClick,
        isDownloadable,
        icon,
        buttonLabel,
        userAction,
    } = useDownloadButton({
        item,
        onClick,
        label,
        showIcon,
    });

    const isPlaceholder = usePlaceholderButton({
        isPlaceholderAllowed,
        isActivated: downloadableItem.isDownloaded || isLoading,
    });

    return isPlaceholder ? (
        <PlaceholderButton />
    ) : (
        <ActionButton
            data-component="DownloadButton"
            icon={icon}
            onClick={handleClick}
            isLoading={isLoading}
            userAction={userAction}
            aria-label={label}
            label={buttonLabel}
            isDisabled={!isDownloadable}
            authOnly
            {...props}
        />
    );
};
